import { pbkdf2 } from 'crypto';
import { randomBytes, secretbox } from 'tweetnacl';
import bip32 from './bip32-factory';
import bs58 from 'bs58';
import { EventEmitter } from 'events';
import { isExtension } from './utils';
import { useEffect, useState } from 'react';
import { chromeLocalStorage } from './chrome-storage-polyfill';

// Minimum password length for new wallets; legacy wallets keep their length.
export const MIN_PASSWORD_LENGTH = 12;
// PBKDF2 iterations for new/upgraded wallets (OWASP baseline for SHA-256).
const KDF_ITERATIONS = 600000;
// Build switch (REACT_APP_ALLOW_PLAINTEXT_SEED=true): permits password-less,
// unencrypted wallets. Reopens SEC-03; delete the branches it guards to retire.
export const ALLOW_PLAINTEXT_SEED =
  process.env.REACT_APP_ALLOW_PLAINTEXT_SEED === 'true';
const PLAINTEXT_SKIP_KEY = 'plaintextSeedSkipped';

export function normalizeMnemonic(mnemonic) {
  return mnemonic.trim().split(/\s+/g).join(' ');
}

export async function generateMnemonicAndSeed(strength = 128) {
  const bip39 = await import('bip39');
  const mnemonic = bip39.generateMnemonic(strength);
  const seed = await bip39.mnemonicToSeed(mnemonic);
  return { mnemonic, seed: Buffer.from(seed).toString('hex') };
}

export async function mnemonicToSeed(mnemonic) {
  const bip39 = await import('bip39');
  if (!bip39.validateMnemonic(mnemonic)) {
    throw new Error('Invalid seed words');
  }
  const seed = await bip39.mnemonicToSeed(mnemonic);
  return Buffer.from(seed).toString('hex');
}

async function getExtensionUnlockedMnemonic() {
  if (!isExtension) {
    return null;
  }

  return new Promise((resolve) => {
    chrome.runtime.sendMessage(
      {
        channel: 'dwallet_extension_mnemonic_channel',
        method: 'get',
      },
      resolve,
    );
  });
}

const EMPTY_MNEMONIC = {
  mnemonic: null,
  seed: null,
  importsEncryptionKey: null,
  derivationPath: null,
};

let unlockedMnemonicAndSeed = (async () => {
  const unlockedExpiration = localStorage.getItem('unlockedExpiration');
  // Left here to clean up stored mnemonics from previous method
  if (unlockedExpiration && Number(unlockedExpiration) < Date.now()) {
    localStorage.removeItem('unlocked');
    localStorage.removeItem('unlockedExpiration');
  }
  const stored = JSON.parse(
    (await getExtensionUnlockedMnemonic()) ||
      sessionStorage.getItem('unlocked') ||
      localStorage.getItem('unlocked') ||
      'null',
  );
  if (stored === null) {
    return EMPTY_MNEMONIC;
  }
  return {
    importsEncryptionKey: deriveImportsEncryptionKey(stored.seed),
    ...stored,
  };
})();

export const walletSeedChanged = new EventEmitter();

export function getUnlockedMnemonicAndSeed() {
  return unlockedMnemonicAndSeed;
}

// returns [mnemonic, loading]
export function useUnlockedMnemonicAndSeed() {
  const [currentUnlockedMnemonic, setCurrentUnlockedMnemonic] = useState(null);

  useEffect(() => {
    walletSeedChanged.addListener('change', setCurrentUnlockedMnemonic);
    unlockedMnemonicAndSeed.then(setCurrentUnlockedMnemonic);
    return () => {
      walletSeedChanged.removeListener('change', setCurrentUnlockedMnemonic);
    };
  }, []);

  return !currentUnlockedMnemonic
    ? [EMPTY_MNEMONIC, true]
    : [currentUnlockedMnemonic, false];
}

export function useHasLockedMnemonicAndSeed() {
  const [unlockedMnemonic, loading] = useUnlockedMnemonicAndSeed();

  return [!unlockedMnemonic.seed && !!localStorage.getItem('locked'), loading];
}

function setUnlockedMnemonicAndSeed(
  mnemonic,
  seed,
  importsEncryptionKey,
  derivationPath,
  extra = {},
) {
  const data = {
    mnemonic,
    seed,
    importsEncryptionKey,
    derivationPath,
    ...extra,
  };
  unlockedMnemonicAndSeed = Promise.resolve(data);
  walletSeedChanged.emit('change', data);
}

// True while the wallet is unlocked with a password below the minimum (SEC-03).
export function useNeedsPasswordUpgrade() {
  const [unlockedMnemonic] = useUnlockedMnemonicAndSeed();
  return !!unlockedMnemonic.passwordTooWeak;
}

// Re-encrypts the unlocked wallet under a compliant replacement password.
export async function upgradeWeakPassword(newPassword) {
  const { mnemonic, seed, derivationPath } = await unlockedMnemonicAndSeed;
  await storeMnemonicAndSeed(mnemonic, seed, newPassword, derivationPath);
}

export async function storeMnemonicAndSeed(
  mnemonic,
  seed,
  password,
  derivationPath,
) {
  if (!password && ALLOW_PLAINTEXT_SEED) {
    return storePlaintext(mnemonic, seed, derivationPath);
  }
  if (!password || password.length < MIN_PASSWORD_LENGTH) {
    throw new Error(
      `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
    );
  }
  return encryptAndStore(
    mnemonic,
    seed,
    password,
    derivationPath,
    KDF_ITERATIONS,
  );
}

// Unencrypted storage; reachable only under ALLOW_PLAINTEXT_SEED.
function storePlaintext(mnemonic, seed, derivationPath) {
  localStorage.setItem(
    'unlocked',
    JSON.stringify({ mnemonic, seed, derivationPath }),
  );
  localStorage.removeItem('locked');
  localStorage.setItem(PLAINTEXT_SKIP_KEY, '1');
  commitUnlocked(mnemonic, seed, derivationPath);
}

// Clears session copies and publishes the unlocked seed in memory.
function commitUnlocked(mnemonic, seed, derivationPath) {
  sessionStorage.removeItem('unlocked');
  if (isExtension) {
    chrome.runtime.sendMessage({
      channel: 'dwallet_extension_mnemonic_channel',
      method: 'set',
      data: '',
    });
  }
  setUnlockedMnemonicAndSeed(
    mnemonic,
    seed,
    deriveImportsEncryptionKey(seed),
    derivationPath,
  );
}

// Encrypts the seed to localStorage['locked'] and drops any plaintext copy.
async function encryptAndStore(
  mnemonic,
  seed,
  password,
  derivationPath,
  iterations,
) {
  const plaintext = JSON.stringify({ mnemonic, seed, derivationPath });
  const salt = randomBytes(16);
  const kdf = 'pbkdf2';
  const digest = 'sha256';
  const key = await deriveEncryptionKey(password, salt, iterations, digest);
  const nonce = randomBytes(secretbox.nonceLength);
  const encrypted = secretbox(Buffer.from(plaintext), nonce, key);
  localStorage.setItem(
    'locked',
    JSON.stringify({
      encrypted: bs58.encode(encrypted),
      nonce: bs58.encode(nonce),
      kdf,
      salt: bs58.encode(salt),
      iterations,
      digest,
    }),
  );
  localStorage.removeItem('unlocked');
  localStorage.removeItem(PLAINTEXT_SKIP_KEY);
  commitUnlocked(mnemonic, seed, derivationPath);
}

// True when a plaintext seed is stored and the user has not skipped encryption
// under ALLOW_PLAINTEXT_SEED. With the switch off, every plaintext seed gates.
export function needsSeedEncryption() {
  try {
    const plaintext =
      !!localStorage.getItem('unlocked') && !localStorage.getItem('locked');
    const skipped =
      ALLOW_PLAINTEXT_SEED && !!localStorage.getItem(PLAINTEXT_SKIP_KEY);
    return plaintext && !skipped;
  } catch (error) {
    return false;
  }
}

export function skipSeedEncryption() {
  localStorage.setItem(PLAINTEXT_SKIP_KEY, '1');
}

export function walletHasPassword() {
  return !!localStorage.getItem('locked');
}

// Encrypts a legacy plaintext seed under a newly chosen password.
export async function migratePlaintextSeed(password) {
  const raw = localStorage.getItem('unlocked');
  if (!raw) {
    return;
  }
  const { mnemonic, seed, derivationPath } = JSON.parse(raw);
  await storeMnemonicAndSeed(mnemonic, seed, password, derivationPath);
}

// Confirms a password by attempting to decrypt the stored blob.
export async function verifyPassword(password) {
  const lockedRaw = localStorage.getItem('locked');
  if (!lockedRaw) {
    return false;
  }
  const { encrypted, nonce, salt, iterations, digest } = JSON.parse(lockedRaw);
  const key = await deriveEncryptionKey(
    password,
    bs58.decode(salt),
    iterations,
    digest,
  );
  return !!secretbox.open(bs58.decode(encrypted), bs58.decode(nonce), key);
}

export async function loadMnemonicAndSeed(password, stayLoggedIn) {
  const {
    encrypted: encodedEncrypted,
    nonce: encodedNonce,
    salt: encodedSalt,
    iterations,
    digest,
  } = JSON.parse(localStorage.getItem('locked'));
  const encrypted = bs58.decode(encodedEncrypted);
  const nonce = bs58.decode(encodedNonce);
  const salt = bs58.decode(encodedSalt);
  const key = await deriveEncryptionKey(password, salt, iterations, digest);
  const plaintext = secretbox.open(encrypted, nonce, key);
  if (!plaintext) {
    throw new Error('Incorrect password');
  }
  const decodedPlaintext = Buffer.from(plaintext).toString();
  const { mnemonic, seed, derivationPath } = JSON.parse(decodedPlaintext);
  // Transparently upgrade wallets encrypted with weaker KDF parameters.
  if (iterations < KDF_ITERATIONS || digest !== 'sha256') {
    await encryptAndStore(
      mnemonic,
      seed,
      password,
      derivationPath,
      KDF_ITERATIONS,
    );
  }
  // Weak passwords are flagged for replacement; the flag rides with the session
  // copy so a reload cannot skip the gate (SEC-03).
  const passwordTooWeak = password.length < MIN_PASSWORD_LENGTH;
  if (stayLoggedIn) {
    const sessionCopy = JSON.stringify({
      mnemonic,
      seed,
      derivationPath,
      passwordTooWeak,
    });
    if (isExtension) {
      chrome.runtime.sendMessage({
        channel: 'dwallet_extension_mnemonic_channel',
        method: 'set',
        data: sessionCopy,
      });
    } else {
      sessionStorage.setItem('unlocked', sessionCopy);
    }
  }
  const importsEncryptionKey = deriveImportsEncryptionKey(seed);
  setUnlockedMnemonicAndSeed(
    mnemonic,
    seed,
    importsEncryptionKey,
    derivationPath,
    { passwordTooWeak },
  );
  return { mnemonic, seed, derivationPath };
}

async function deriveEncryptionKey(password, salt, iterations, digest) {
  return new Promise((resolve, reject) =>
    pbkdf2(
      password,
      salt,
      iterations,
      secretbox.keyLength,
      digest,
      (err, key) => (err ? reject(err) : resolve(key)),
    ),
  );
}

export function lockWallet() {
  try {
    sessionStorage.removeItem('unlocked');
  } catch (error) {
    // sessionStorage may be unavailable; in-memory clear below still applies.
  }
  if (isExtension) {
    chrome.runtime.sendMessage({
      channel: 'dwallet_extension_mnemonic_channel',
      method: 'set',
      data: '',
    });
  }
  setUnlockedMnemonicAndSeed(null, null, null, null);
}

// Returns the 32 byte key used to encrypt imported private keys.
function deriveImportsEncryptionKey(seed) {
  // SLIP16 derivation path.
  return bip32.fromSeed(Buffer.from(seed, 'hex')).derivePath("m/10016'/0")
    .privateKey;
}

export function forgetWallet() {
  localStorage.clear();
  sessionStorage.removeItem('unlocked');
  if (isExtension) {
    chrome.runtime.sendMessage({
      channel: 'dwallet_extension_mnemonic_channel',
      method: 'set',
      data: '',
    });
  }
  unlockedMnemonicAndSeed = {
    mnemonic: null,
    seed: null,
    importsEncryptionKey: null,
  };
  walletSeedChanged.emit('change', unlockedMnemonicAndSeed);
  if (isExtension) {
    // Must use wrapper function for window.location.reload
    chromeLocalStorage.clear(() => window.location.reload());
  } else {
    window.location.reload();
  }
}
